import React from 'react';
import {
	Box,
	Flex,
	Heading,
	HStack,
	Text,
	Badge,
	VStack,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import {
	useAuthFlow,
	AUTH_MODES,
} from '../../../services/Contexts/AuthFlowContext';
import { useAuthBranding } from '../../../services/Contexts/AuthBrandingContext';
import { getPortalSubdomain } from '../../../utils/subdomainUtils';
import SignInForm from './Login';
import SignUpForm from './RegisterUserForm';
import ForgotPasswordForm from './RequestPasswordReset';
import ResetPasswordForm from './ResetPassword';
import AuthErrorDisplay from './AuthErrorDisplay';
import LoginPageBgImg from '../../../assets/crm.webp';
import LogoImage from '../../../assets/logoWhite.png';
import pageTokens from '../../../styles/pageTokens';
import AuroraBackground from '../../../pages/OfflinePages/HomePage/AuroraBackground';
import { HERO_BG_BASE } from '../../../pages/OfflinePages/HomePage/homePageVisuals';
import {
	FaEnvelope,
	FaRocket,
	FaChartLine,
	FaUsers,
	FaLock,
} from 'react-icons/fa';

const MotionBox = motion(Box);

const spring = {
	type: 'spring',
	stiffness: 90,
	damping: 20,
};

const hexToRgba = (hex, alpha) => {
	if (!hex?.startsWith('#')) return hex || `rgba(0, 0, 0, ${alpha})`;
	const normalized = hex.replace('#', '');
	const value =
		normalized.length === 3
			? normalized
					.split('')
					.map((char) => char + char)
					.join('')
			: normalized.slice(0, 6);
	const r = parseInt(value.slice(0, 2), 16);
	const g = parseInt(value.slice(2, 4), 16);
	const b = parseInt(value.slice(4, 6), 16);
	if ([r, g, b].some(Number.isNaN)) return `rgba(0, 0, 0, ${alpha})`;
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const BubbleIcon = ({ icon: IconComp, top, left, right, bottom, size, delay, color, opacity = 0.2 }) => (
	<motion.div
		style={{
			position: 'absolute',
			top,
			left,
			right,
			bottom,
			zIndex: 0,
			opacity,
			pointerEvents: 'none',
		}}
		initial={{ y: 0, scale: 0.85 }}
		animate={{ y: [0, -28, 0], scale: [0.85, 1.08, 0.85] }}
		transition={{
			duration: 6,
			repeat: Infinity,
			repeatType: 'reverse',
			delay,
			ease: 'easeInOut',
		}}>
		<IconComp size={size} color={color} />
	</motion.div>
);

const PANEL_BUBBLES = [
	{ icon: FaRocket, top: '8%', left: '8%', size: 52, delay: 0, color: '#fff' },
	{ icon: FaChartLine, top: '58%', left: '14%', size: 36, delay: 1.2, color: '#fff' },
	{ icon: FaUsers, top: '22%', left: '72%', size: 42, delay: 2.1, color: '#fff' },
	{ icon: FaLock, top: '78%', left: '76%', size: 30, delay: 3.2, color: '#fff' },
	{ icon: FaEnvelope, top: '42%', left: '48%', size: 28, delay: 1.8, color: '#fff' },
];

const PAGE_BUBBLES = [
	{ icon: FaRocket, top: '12%', left: '6%', size: 40, delay: 0.5, color: '#0d9488', opacity: 0.12 },
	{ icon: FaChartLine, bottom: '18%', right: '8%', size: 34, delay: 2, color: '#0891b2', opacity: 0.1 },
	{ icon: FaUsers, top: '20%', right: '12%', size: 30, delay: 3.5, color: '#14b8a6', opacity: 0.1 },
];

/** Large faded circle — matches logged-in PageContent decor */
const AuthBackgroundDecor = () => (
	<Box
		position='absolute'
		top={{ base: '-100px', md: '-140px' }}
		right={{ base: '-100px', md: '-160px' }}
		w={{ base: '320px', md: '480px', lg: '700px' }}
		h={{ base: '320px', md: '480px', lg: '700px' }}
		borderRadius='full'
		bg={pageTokens.bg}
		opacity={pageTokens.decorCircleOpacity}
		pointerEvents='none'
		zIndex={0}
		aria-hidden
	/>
);

const MODE_LABELS = {
	[AUTH_MODES.SIGN_IN]: 'Sign in',
	[AUTH_MODES.SIGN_UP]: 'Create account',
	[AUTH_MODES.FORGOT_PASSWORD]: 'Reset access',
	[AUTH_MODES.RESET_PASSWORD]: 'New password',
};

const DEFAULT_FEATURES = [
	{
		Ic: FaChartLine,
		label: 'Projects & clients',
		desc: 'Track every client and project in one place.',
	},
	{
		Ic: FaUsers,
		label: 'Team management',
		desc: 'Assign tasks and monitor your team.',
	},
	{
		Ic: FaEnvelope,
		label: 'Email campaigns',
		desc: 'Launch targeted campaigns effortlessly.',
	},
	{
		Ic: FaLock,
		label: 'Secure & reliable',
		desc: 'Enterprise-grade security for your data.',
	},
];

const AuthContainer = () => {
	const { mode } = useAuthFlow();
	const hideFormLogoOnSignup = window?.location?.pathname === '/signup';

	const contextBranding = useAuthBranding()?.branding || {};
	const authBrandData = contextBranding.auth || {};

	const displayLogoOnLoginScreen = {
		displayLogoOnForm:
			authBrandData.displayLogoOnLoginScreen?.displayLogoOnForm ??
			contextBranding.displayLogoOnLoginScreen?.displayLogoOnForm ??
			true,
		displayLogoOnPanel:
			authBrandData.displayLogoOnLoginScreen?.displayLogoOnPanel ??
			contextBranding.displayLogoOnLoginScreen?.displayLogoOnPanel ??
			true,
		displayCompanyNameOnForm:
			authBrandData.displayLogoOnLoginScreen?.displayCompanyNameOnForm ??
			contextBranding.displayLogoOnLoginScreen?.displayCompanyNameOnForm ??
			true,
	};

	const authBranding = {
		authBackgroundImage:
			authBrandData.authBackgroundImage ?? contextBranding.authBackgroundImage,
		panelBackgroundColor:
			authBrandData.panelBackgroundColor ?? contextBranding.panelBackgroundColor,
		panelTextColor:
			authBrandData.panelTextColor ?? contextBranding.panelTextColor,
		penelText: authBrandData.penelText ?? contextBranding.penelText,
		penelTextSize: authBrandData.penelTextSize ?? contextBranding.penelTextSize,
		panelCustomMessage:
			authBrandData.panelCustomMessage ?? contextBranding.panelCustomMessage,
		panelCustomMessageTextColor:
			authBrandData.panelCustomMessageTextColor ??
			contextBranding.panelCustomMessageTextColor,
		panelCustomMessageTextSize:
			authBrandData.panelCustomMessageTextSize ??
			contextBranding.panelCustomMessageTextSize,
		formCustomMessageTextColor:
			authBrandData.formCustomMessageTextColor ??
			contextBranding.formCustomMessageTextColor,
		formCustomMessageTextSize:
			authBrandData.formCustomMessageTextSize ??
			contextBranding.formCustomMessageTextSize,
		formCompanyNameTextColor:
			authBrandData.formCompanyNameTextColor ??
			contextBranding.formCompanyNameTextColor,
		panelLogoUseGlassEffect:
			authBrandData.panelLogoUseGlassEffect ??
			contextBranding.panelLogoUseGlassEffect,
		panelLogoBackgroundColor:
			authBrandData.panelLogoBackgroundColor ??
			contextBranding.panelLogoBackgroundColor,
		logo: authBrandData.logo ?? contextBranding.logo,
		brandName:
			authBrandData.brandName ??
			contextBranding.companyName ??
			contextBranding.name,
	};

	const authCompanyName = contextBranding.companyName || contextBranding.name;
	const infoPanelBackgroundImage = authBranding.authBackgroundImage;
	const hasCustomAuthAssets = Boolean(
		authBranding.logo ||
			infoPanelBackgroundImage ||
			authBranding.panelCustomMessage ||
			authBrandData.panelBackgroundColor ||
			authBrandData.authBackgroundImage,
	);
	const hasBrandingAccess =
		typeof contextBranding.hasBrandingAccess === 'boolean'
			? contextBranding.hasBrandingAccess
			: hasCustomAuthAssets;
	const hasBranding =
		hasBrandingAccess &&
		Boolean(
			authCompanyName ||
				authBranding.logo ||
				infoPanelBackgroundImage ||
				authBranding.panelCustomMessage,
		);

	const infoPanelBackgroundColor =
		hasBrandingAccess && (authBranding.panelBackgroundColor || contextBranding.sidebarBackgroundColor)
			? authBranding.panelBackgroundColor || contextBranding.sidebarBackgroundColor
			: HERO_BG_BASE;
	const resolvedInfoPanelBackgroundImage =
		hasBrandingAccess && infoPanelBackgroundImage ? infoPanelBackgroundImage : null;
	const panelMessageTextColor =
		authBranding.panelCustomMessageTextColor ||
		authBranding.panelTextColor ||
		'#ffffff';
	const panelMessageTextSize = authBranding.panelCustomMessageTextSize || '16px';
	const formMessageTextColor =
		authBranding.formCustomMessageTextColor ||
		authBranding.panelTextColor ||
		'#334155';
	const formMessageTextSize =
		authBranding.formCustomMessageTextSize || authBranding.penelTextSize || '16px';
	const formCompanyNameTextColor =
		authBranding.formCompanyNameTextColor ||
		authBranding.formCustomMessageTextColor ||
		authBranding.panelTextColor ||
		'#0F172A';

	const authBrandName = authBranding.brandName || 'Ops Pilot CRM';
	const authLogo = authBranding.logo || LogoImage;
	const currentSubdomain = getPortalSubdomain(window?.location?.hostname || '');
	const canShowSignUp = !currentSubdomain;

	const formFirst = mode === AUTH_MODES.SIGN_UP;
	const showCompanyNameOnForm =
		hasBrandingAccess &&
		displayLogoOnLoginScreen.displayCompanyNameOnForm &&
		Boolean(authCompanyName) &&
		mode === AUTH_MODES.SIGN_IN;
	const panelLogoUseGlassEffect = authBranding.panelLogoUseGlassEffect !== false;
	const panelLogoBackgroundColor =
		authBranding.panelLogoBackgroundColor || 'rgba(0, 0, 0, 0.4)';
	const panelLogoContainerBg = panelLogoBackgroundColor.startsWith('#')
		? panelLogoUseGlassEffect
			? hexToRgba(panelLogoBackgroundColor, 0.4)
			: panelLogoBackgroundColor
		: panelLogoBackgroundColor;

	const getDynamicContent = () => {
		switch (mode) {
			case AUTH_MODES.SIGN_UP:
				if (!canShowSignUp) {
					return {
						title: 'Sign in',
						description:
							'Sign-up is disabled on portal subdomains. Please sign in using your company portal.',
						form: <SignInForm showTitle={false} />,
					};
				}
				return {
					title: 'Create your workspace',
					description: 'Start your Ops Pilot CRM account in minutes.',
					form: <SignUpForm showTitle={false} />,
				};
			case AUTH_MODES.FORGOT_PASSWORD:
				return {
					title: 'Forgot your password?',
					description:
						"We'll email you a secure link to choose a new password.",
					form: <ForgotPasswordForm showTitle={false} />,
				};
			case AUTH_MODES.RESET_PASSWORD:
				return {
					title: 'Set a new password',
					description: 'Choose a strong password for your account.',
					form: <ResetPasswordForm showTitle={false} />,
				};
			case AUTH_MODES.SIGN_IN:
			default:
				return {
					title:
						displayLogoOnLoginScreen.displayCompanyNameOnForm && authCompanyName
							? authCompanyName
							: 'Welcome back',
					description: authBranding.penelText
						? authBranding.penelText
						: authCompanyName
							? 'Sign in to your company portal.'
							: 'Sign in to continue to Ops Pilot CRM.',
					form: <SignInForm showTitle={false} />,
				};
		}
	};

	const content = getDynamicContent();
	const modeLabel = MODE_LABELS[mode] || 'Account';

	const renderFormColumn = () => (
		<MotionBox
			key={`${mode}-form`}
			flex='1 1 0%'
			minW={{ base: 'full', md: '0' }}
			bg='white'
			p={{ base: 6, md: 10, lg: 12 }}
			display='flex'
			flexDirection='column'
			justifyContent='center'
			layout
			transition={spring}>
			<VStack spacing={6} align='stretch' w='full' maxW='420px' mx='auto'>
				{displayLogoOnLoginScreen.displayLogoOnForm &&
					!hideFormLogoOnSignup &&
					hasBrandingAccess &&
					authBranding.logo && (
						<Box textAlign='center'>
							<img
								src={authBranding.logo}
								alt={authBrandName}
								style={{ maxWidth: '160px', maxHeight: '64px', objectFit: 'contain' }}
							/>
						</Box>
					)}

				<Box>
					<Badge
						colorScheme='teal'
						variant='subtle'
						borderRadius='full'
						px={3}
						py={1}
						mb={3}
						fontSize='xs'
						textTransform='uppercase'
						letterSpacing='wider'>
						{modeLabel}
					</Badge>
					<Heading
						as='h1'
						size='lg'
						color={
							showCompanyNameOnForm ? formCompanyNameTextColor : 'gray.900'
						}
						mb={2}
						fontWeight='800'
						lineHeight='shorter'>
						{content.title}
					</Heading>
					<Text
						color={formMessageTextColor}
						fontSize={mode === AUTH_MODES.SIGN_IN ? formMessageTextSize : 'md'}
						lineHeight='tall'
						mb={0}>
						{content.description}
					</Text>
				</Box>

				<AuthErrorDisplay />
				{content.form}
			</VStack>
		</MotionBox>
	);

	const renderInfoColumn = () => (
		<MotionBox
			key='info-box'
			flex='1 1 0%'
			minW={{ md: '0' }}
			bg={infoPanelBackgroundColor}
			bgImage={
				resolvedInfoPanelBackgroundImage
					? `url(${resolvedInfoPanelBackgroundImage})`
					: `url(${LoginPageBgImg})`
			}
			bgSize='cover'
			bgPosition='center'
			color='white'
			p={{ base: 8, md: 10, lg: 12 }}
			display={{ base: 'none', md: 'flex' }}
			flexDirection='column'
			justifyContent='center'
			alignItems='center'
			position='relative'
			overflow='hidden'
			layout
			transition={spring}>
			<AuroraBackground />

			{PANEL_BUBBLES.map((bubble, index) => (
				<BubbleIcon key={`panel-bubble-${index}`} {...bubble} opacity={0.22} />
			))}

			<Box zIndex={1} textAlign='center' w='100%' maxW='380px'>
				{hasBranding ? (
					<>
						{displayLogoOnLoginScreen.displayLogoOnPanel && (
							<VStack
								spacing={6}
								bg={panelLogoContainerBg}
								p={8}
								borderRadius='2xl'
								backdropFilter={panelLogoUseGlassEffect ? 'blur(12px)' : undefined}
								border='1px solid'
								borderColor={panelLogoUseGlassEffect ? 'whiteAlpha.300' : 'transparent'}>
								<img
									src={authLogo}
									alt={`${authBrandName} Logo`}
									style={{ maxWidth: '220px', objectFit: 'contain' }}
								/>
							</VStack>
						)}
						{authBranding.panelCustomMessage && (
							<Text
								mt={displayLogoOnLoginScreen.displayLogoOnPanel ? 6 : 0}
								fontSize={panelMessageTextSize}
								color={panelMessageTextColor}
								lineHeight='tall'>
								{authBranding.panelCustomMessage}
							</Text>
						)}
					</>
				) : (
					<>
						{displayLogoOnLoginScreen.displayLogoOnPanel && (
							<img
								src={authLogo}
								alt={`${authBrandName} Logo`}
								style={{
									maxWidth: '200px',
									marginBottom: '20px',
									display: 'inline-block',
								}}
							/>
						)}
						<Heading size='md' fontWeight='800' mb={2}>
							Run your agency from one cockpit
						</Heading>
						<Text fontSize='sm' color='whiteAlpha.800' mb={6} lineHeight='tall'>
							CRM, campaigns, projects, and invoicing — built for freelancers and
							agencies.
						</Text>
						{authBranding.panelCustomMessage && (
							<Text
								mb={6}
								fontSize={panelMessageTextSize}
								color={panelMessageTextColor}
								lineHeight='tall'>
								{authBranding.panelCustomMessage}
							</Text>
						)}
						<VStack spacing={3} align='stretch'>
							{DEFAULT_FEATURES.map(({ Ic, label, desc }) => (
								<HStack
									key={label}
									spacing={4}
									bg='whiteAlpha.150'
									borderRadius='xl'
									p={4}
									align='center'
									border='1px solid'
									borderColor='whiteAlpha.200'
									backdropFilter='blur(8px)'>
									<Flex
										w='40px'
										h='40px'
										borderRadius='lg'
										bg='whiteAlpha.250'
										align='center'
										justify='center'
										flexShrink={0}>
										<Ic size={18} />
									</Flex>
									<Box textAlign='left'>
										<Text fontWeight='bold' fontSize='sm' mb={0.5}>
											{label}
										</Text>
										<Text fontSize='xs' color='whiteAlpha.800' lineHeight='1.4' mb={0}>
											{desc}
										</Text>
									</Box>
								</HStack>
							))}
						</VStack>
					</>
				)}
			</Box>
		</MotionBox>
	);

	return (
		<Flex
			minH='100vh'
			align='center'
			justify='center'
			px={{ base: 4, md: 6 }}
			py={{ base: 6, md: 10 }}
			position='relative'
			overflow='hidden'
			sx={{ background: pageTokens.pageBackgroundGradient }}>
			<AuthBackgroundDecor />
			{PAGE_BUBBLES.map((bubble, index) => (
				<BubbleIcon key={`page-bubble-${index}`} {...bubble} />
			))}

			<Flex
				position='relative'
				zIndex={1}
				w='full'
				maxW='1080px'
				minH={{ md: '620px' }}
				bg='white'
				borderRadius='3xl'
				boxShadow='0 24px 64px rgba(15, 23, 42, 0.12)'
				border='1px solid'
				borderColor='gray.100'
				overflow='hidden'
				direction={{ base: 'column', md: 'row' }}>
				{formFirst ? (
					<>
						{renderFormColumn()}
						{renderInfoColumn()}
					</>
				) : (
					<>
						{renderInfoColumn()}
						{renderFormColumn()}
					</>
				)}
			</Flex>
		</Flex>
	);
};

export default AuthContainer;
